// React
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'

// Libraries
import _get from 'lodash/get'
import _find from 'lodash/find'
import _findIndex from 'lodash/findIndex'

// Components
import FocalImage from 'ui/components/FocalImage'
import Icon from 'ui/components/Icon'
import BProductImage from 'store/modules/product/blocks/ProductImage'
import ImageMagnify from 'store/modules/product/components/ImageMagnify'
import ModalGallery from 'store/modules/product/components/ModalGallery'

// Shared
import useIsMobile from 'shared/utils/useIsMobile'
import NO_PRODUCT_IMAGE from '@assets/images/no-product-image.png'

const THUMBNAIL_SIZE = 60

export class ProductImage extends Component {
  static displayName = 'ProductImage'

  static propTypes = {
    defaultPhoto: PropTypes.object,
    // Show this photo
    photoId: PropTypes.string,
    brandingColor: PropTypes.string,
    portalTarget: PropTypes.instanceOf(Element),
    itemTitle: PropTypes.any,
    alt: PropTypes.string,
    isMobile: PropTypes.bool
  }

  constructor (props) {
    super(props)

    this.state = {
      selectedPhoto: null,
      galleryOffset: 0,
      magnify: false,
      showGallery: false,
      hideFocalImages: false
    }
  }

  componentDidMount = () => {
    this.componentDidUpdate({})
  }

  componentDidUpdate = (prevProps) => {
    const { defaultPhoto, photoId } = this.props

    if (photoId !== prevProps.photoId) {
      let newSelectedPhoto

      if (photoId) {
        newSelectedPhoto = _find([defaultPhoto, ...this.photos], { id: photoId })
      } else {
        newSelectedPhoto = defaultPhoto
      }

      const newState = { selectedPhoto: newSelectedPhoto }

      // Makes sure we update the gallery offset if needed
      const photoIndex = this.photos.indexOf(newSelectedPhoto)

      if (photoIndex <= -1) {
        newState.galleryOffset = 0
      } else {
        newState.galleryOffset = Math.min(photoIndex, this.maximumOffset)
      }

      this.setState(newState)
    }
  }

  get maximumOffset () {
    // Displaying 3 images at a time the default product group
    // image + 2 album_photos so maximumOffset is how many images
    // over these 3
    return Math.max(this.photos.length - 2, 0)
  }

  get photos () {
    return _get(this.props, 'albumPhotos', [])
  }

  handleChangeImage = (photo) => {
    this.setState({ selectedPhoto: photo, magnify: false, hideFocalImages: false })
  }

  handleImagesForward = () => {
    const { galleryOffset } = this.state

    if (galleryOffset + 1 > this.maximumOffset) return null

    this.setState({ galleryOffset: galleryOffset + 1 })
  }

  handleImagesBackward = () => {
    const { galleryOffset } = this.state

    if (galleryOffset - 1 < 0) return null

    this.setState({ galleryOffset: galleryOffset - 1 })
  }

  handleClick = () => {
    const { magnify, hideFocalImages } = this.state
    const { defaultPhoto } = this.props

    const canMagnify = [defaultPhoto, ...this.photos][0] !== undefined

    if (canMagnify) {
      this.setState({ magnify: !magnify, hideFocalImages: !hideFocalImages })
    }
  }

  handleTouch = () => {
    const { defaultPhoto } = this.props

    const canMagnify = [defaultPhoto, ...this.photos][0] !== undefined

    if (canMagnify) {
      this.setState({ showGallery: true })
    }
  }

  handleGalleryClose = (e) => {
    e.preventDefault()

    this.setState({ showGallery: false })
  }

  handleThumbnailPress = (selectedPhoto) => {
    this.setState({ selectedPhoto })
  }

  handlePhotoSelect = (direction) => {
    const { selectedPhoto } = this.state
    const { defaultPhoto } = this.props

    const photosArray = [defaultPhoto, ...this.photos]

    const selectedPhotoURL = (selectedPhoto && selectedPhoto.large_url) || (defaultPhoto && defaultPhoto.large_url)

    const currentIndex = _findIndex(photosArray, { large_url: selectedPhotoURL })

    if (direction === -1 && currentIndex === 0) return

    if (direction === 1 && currentIndex === photosArray.length - 1) return

    const newSelectedPhoto = photosArray[currentIndex + direction]

    this.setState({ selectedPhoto: newSelectedPhoto })
  }

  render () {
    const { selectedPhoto, galleryOffset, magnify, showGallery: showMobileGallery, hideFocalImages } = this.state
    const { defaultPhoto, brandingColor, portalTarget, itemTitle, alt, isMobile } = this.props

    const selectedPhotoURL = (selectedPhoto && selectedPhoto.large_url) || (defaultPhoto && defaultPhoto.large_url)
    const selectedLargePhotoURL = (selectedPhoto && selectedPhoto.xlarge_url) || (defaultPhoto && defaultPhoto.xlarge_url)

    const showGallery = this.photos.length > 0
    const showControls = this.photos.length > 2

    const canMagnify = [defaultPhoto, ...this.photos][0] !== undefined

    return (
      <BProductImage>
        <BProductImage.MainImageContainer
          data-tid="Main image"
          onClick={isMobile ? this.handleTouch : this.handleClick}
          magnify={magnify}
          canMagnify={canMagnify}
        >
          {[defaultPhoto, ...this.photos].map((photo, index) => (
            <BProductImage.MainImage visible={!hideFocalImages && selectedPhotoURL === photo?.large_url} key={photo?.id || `no_photo_${index}`}>
              <FocalImage
                url={photo?.large_url || NO_PRODUCT_IMAGE}
                preview={photo?.preview}
                coordinates={photo?.coordinates || { x: 0, y: 0 }}
                size="100%"
                alt={alt}
                noBorder
                noBorderRadius
              />
            </BProductImage.MainImage>
          ))}
          {canMagnify && magnify && !isMobile && (
            <ImageMagnify
              originalImage={selectedPhotoURL}
              zoomedImage={selectedLargePhotoURL}
              portalTarget={portalTarget}
              size={250}
              zoomedSize={350}
              scale={2}
            />
          )}
          {showMobileGallery && isMobile && (
            <ModalGallery
              selectedPhoto={selectedPhoto || defaultPhoto}
              photos={[defaultPhoto, ...this.photos]}
              onClose={this.handleGalleryClose}
              handlePhotoSelect={this.handlePhotoSelect}
              brandingColor={brandingColor}
              itemTitle={itemTitle}
              onThumbnailPress={this.handleThumbnailPress}
            />
          )}
        </BProductImage.MainImageContainer>
        {showGallery && (
          <BProductImage.Gallery size={THUMBNAIL_SIZE} offset={galleryOffset}>
            {showControls && (
              <BProductImage.Controls onClick={this.handleImagesBackward} disabled={galleryOffset <= 0}>
                <Icon icon="chevron-left" />
              </BProductImage.Controls>
            )}
            <BProductImage.GalleryImagesContainer>
              <BProductImage.GalleryImages data-tid="Product image gallery">
                {[defaultPhoto, ...this.photos].map((photo) => (
                  <BProductImage.GalleryImageContainer
                    key={photo.id}
                    onClick={this.handleChangeImage.bind(null, photo)}
                    selected={selectedPhoto && selectedPhoto.id === photo.id}
                    brandingColor={brandingColor}
                  >
                    <FocalImage
                      url={photo.large_url}
                      preview={_get(photo, 'preview')}
                      coordinates={photo.coordinates}
                      size={`${THUMBNAIL_SIZE}px`}
                      noBorder
                      noBorderRadius
                    />
                  </BProductImage.GalleryImageContainer>
                ))}
              </BProductImage.GalleryImages>
            </BProductImage.GalleryImagesContainer>
            {showControls && (
              <BProductImage.Controls onClick={this.handleImagesForward} disabled={galleryOffset >= this.maximumOffset}>
                <Icon icon="chevron-right" />
              </BProductImage.Controls>
            )}
          </BProductImage.Gallery>
        )}
      </BProductImage>
    )
  }
}

const ProductImageWrapper = (props) => {
  const isMobile = useIsMobile()

  return <ProductImage {...props} isMobile={isMobile} />
}

const mapStateToProps = (state, props) => {
  const photos = state.orm.photos
    .getAll({ owner_id: props.ownerId })
    .sort('position')
    .toArray()

  const defaultPhoto = photos && photos[0]
  const albumPhotos = photos && photos.slice(1)

  return {
    brandingColor: state.settings.get('store.brand_color'),
    defaultPhoto,
    albumPhotos
  }
}

export default reduxConnect(mapStateToProps)(ProductImageWrapper)
